// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Libs
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
// Components
import ChartCard from '@/common-utils/components/renders/chartsCards/ChartCard';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from '@/common-utils/components/ui/chart';
import CardRedirection from '../../common/CardRedirection';
// Common
import VerticalBarChartSkeleton from '../../common/VerticalBarChartSkeleton';
import generateColorFromKey from '../../../utils/generateColorFromKey';
import getTranslatedText from '@/common-utils/functions/getTranslatedText';
import RouteUrls from '@/app/root/enums/RouteUrls';
import SIM_CARDS_STATS_FIELDS from '../../../fields/SimCardsStatsFields';
import CHART_SIM_PROVIDERS from '../../../enums/chartSimProviders';
// Lang
import { nsDashboard, nsSimCard } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.dashboardData
 * @returns {JSX} SimOffersChart
 */
function SimOffersChart({ dashboardData }) {

    // Hooks
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const translateOfferValue = (offer) => {
        const offerUnit = offer.split(' ')[1];
        const offerValue = offer.split(' ')[0];
        const entity = `units.${offerUnit}`;

        return `${offerValue} ${getTranslatedText(entity, nsSimCard)}`;
    };

    // State
    const [chartData, setChartData] = useState([]);
    const [chartConfig, setChartConfig] = useState({});
    const [simProviders, setSimProviders] = useState([]);

    // Hooks
    useEffect(() => {
        if (dashboardData.simCards?.data) {
            const items = dashboardData.simCards?.data?.items || [];

            // Extract all providers from the items
            const providers = [...new Set(items.map((item) => item[SIM_CARDS_STATS_FIELDS.SIM_PROVIDER.key]))]
                .map((provider) => ({
                    key: provider,
                    name: Object.values(CHART_SIM_PROVIDERS).find(item => item.key === provider)?.name
                }))
                .filter(Boolean);

            setSimProviders(providers);

            // Extract all offers from the items
            const simOffers = [...new Set(
                items.flatMap((item) => Object.keys(item.offersBreakdown || {}))
            )
            ].filter(offer => offer.trim() !== '');

            setChartData(simOffers.map((offer) => ({
                offer,
                ...Object.fromEntries(
                    providers.map((provider) => ([
                        provider.key,
                        items.find((s) => s[SIM_CARDS_STATS_FIELDS.SIM_PROVIDER.key] === provider.key)?.offersBreakdown?.[offer] || 0
                    ]))
                )
            })));

            setChartConfig({
                ...Object.fromEntries(providers.map((provider) => [provider.key, {
                    label: provider.name,
                    fill: generateColorFromKey(provider.key)
                }]))
            });
        }
    }, [dashboardData, i18n.language]);

    return (
        <>
            {dashboardData.simCards?.isFetching && <VerticalBarChartSkeleton />}
            {!dashboardData.simCards?.isFetching && (
                <ChartCard
                    title='charts.sim_offers.title'
                    tooltipEntity='charts.sim_offers.tooltip'
                    namespace={nsDashboard}
                >
                    {dashboardData.simCards?.data && !dashboardData.simCards?.error && (
                        <ChartContainer config={chartConfig} className='w-full max-h-[240px]'>
                            <BarChart data={chartData} accessibilityLayer>
                                <CartesianGrid vertical={false} strokeWidth={1} stroke='rgb(229, 231, 235)' />
                                <XAxis
                                    dataKey='offer'
                                    tickLine={false}
                                    tickMargin={8}
                                    axisLine={false}
                                    tick={{ fontWeight: '600' }}
                                    tickFormatter={(value) => translateOfferValue(value)}
                                />
                                <YAxis
                                    width={36}
                                    tickLine={false}
                                    axisLine={false}
                                    tickMargin={8}
                                    allowDecimals={false}
                                    tick={{ fontWeight: '600' }}
                                    tickFormatter={(value) => Number(value).toLocaleString(i18n.language)}
                                />
                                <ChartTooltip
                                    isAnimationActive={false}
                                    cursor={false}
                                    content={<ChartTooltipContent indicator='dashed' formatter={(value, _name, item) => (
                                        <div className='grid w-full min-w-[10rem] gap-1.5'>
                                            <div className='flex w-full items-center justify-between gap-4 leading-none'>
                                                <span className='text-muted-foreground'>
                                                    {chartConfig[item.dataKey]?.label ?? item.dataKey}
                                                </span>
                                                <span className='font-medium text-foreground tabular-nums'>
                                                    {value !== null && value !== undefined && value !== ''
                                                        ? Number(value).toLocaleString(i18n.language)
                                                        : '—'}
                                                </span>
                                            </div>
                                        </div>
                                    )} />}
                                />
                                {simProviders.map((provider) => (
                                    <Bar
                                        maxBarSize={24}
                                        key={provider.key}
                                        dataKey={provider.key}
                                        fill={chartConfig[provider.key]?.fill}
                                        radius={4}
                                    />
                                ))}
                                <ChartLegend
                                    verticalAlign='bottom'
                                    content={<ChartLegendContent />}
                                />
                            </BarChart>
                        </ChartContainer>
                    )}
                    {dashboardData.simCards?.error && (
                        <CardRedirection
                            buttonEntity='redirection.sim_cards'
                            namespace={nsDashboard}
                            onClick={() => { navigate(RouteUrls.SIM_CARDS.url); }}
                        />
                    )}
                </ChartCard>
            )}
        </>
    );
}

SimOffersChart.propTypes = {
    dashboardData: PropTypes.object.isRequired
};

export default SimOffersChart;